/**
 * Single source of truth for converting a product "rate" into an absolute
 * quantity to apply (and to deduct from inventory).
 *
 * Every treatment path (planner task, voice task, direct spray log) must use
 * this module so the resulting absolute amount is identical regardless of how
 * the dose was expressed. Downstream (inventory deduction, spray_log_entries,
 * consumption/analytics reports) only ever reads that absolute amount.
 *
 * Three dosing bases, decided by the unit alone (no extra mode flag):
 *   - absolute:  L, mL, kg, g, units, pkg            -> amount = value
 *   - per_area:  L/ha, kg/ha, L/1000m², kg/1000m²    -> amount = value × area
 *   - per_caldo: mL/100L, g/100L                     -> amount = value × caldo/100
 *
 * Note: cc ≡ mL. The canonical concentration unit is "mL/100L".
 */

import { convertQuantity, normalizeUnit, sameFamily } from "@/lib/unitConversion";

export const PER_AREA_UNITS = ["L/ha", "kg/ha", "L/1000m²", "kg/1000m²"] as const;
export const PER_CALDO_UNITS = ["mL/100L", "g/100L"] as const;

export type DosingBasis = "absolute" | "per_area" | "per_caldo";

export type ResolveErrorCode =
  | "CALDO_REQUIRED"
  | "AREA_REQUIRED"
  | "UNIT_PRODUCT_MISMATCH"
  | "INVALID_VALUE";

export interface ResolveInput {
  /** The number the user typed (the rate or the absolute amount). */
  value: number;
  /** The unit the user picked (absolute, per-area or per-caldo). */
  unit: string;
  /** Total area of the target section(s), in m². Use 0 when unknown. */
  areaM2: number;
  /** Total spray solution (caldo) in liters, already resolved. Null when not provided. */
  caldoL: number | null;
  /** The product's inventory unit (L, mL, kg, g, units, pkg). */
  stockUnit: string;
}

export interface ResolveResult {
  /** Absolute quantity to apply / deduct, in `amountUnit`. 0 when invalid. */
  amount: number;
  /** Unit of the resolved absolute amount. */
  amountUnit: string;
  basis: DosingBasis;
  /** Set when the rate could not be resolved; `amount` is 0 in that case. */
  error?: ResolveErrorCode;
}

const norm = (u: string): string => (u || "").trim().toLowerCase();

const LIQUID_STOCK = new Set(["l", "ml", "cc"]);
const SOLID_STOCK = new Set(["kg", "g"]);
const COUNT_STOCK = new Set(["units", "ud", "u", "unit", "pkg"]);

export function isLiquidStock(stockUnit: string): boolean {
  return LIQUID_STOCK.has(norm(stockUnit));
}
export function isSolidStock(stockUnit: string): boolean {
  return SOLID_STOCK.has(norm(stockUnit));
}
export function isCountStock(stockUnit: string): boolean {
  return COUNT_STOCK.has(norm(stockUnit));
}

export function classifyUnit(unit: string): DosingBasis {
  const u = (unit || "").trim();
  if (/\/\s*100\s*l$/i.test(u)) return "per_caldo";
  if (/\/\s*(ha|1000\s*m²|1000\s*m2)$/i.test(u)) return "per_area";
  return "absolute";
}

/**
 * Units offered for a product, filtered by its physical form so an impossible
 * pairing (e.g. g/100L for a liquid) can never be selected.
 */
export function allowedUnitsForProduct(stockUnit: string): {
  absolute: string[];
  perArea: string[];
  perCaldo: string[];
} {
  if (isSolidStock(stockUnit)) {
    return { absolute: ["kg", "g"], perArea: ["kg/ha", "kg/1000m²"], perCaldo: ["g/100L"] };
  }
  if (isCountStock(stockUnit)) {
    return { absolute: ["units", "pkg"], perArea: [], perCaldo: [] };
  }
  // default: treat as liquid
  return { absolute: ["L", "mL"], perArea: ["L/ha", "L/1000m²"], perCaldo: ["mL/100L"] };
}

/** Whether a (dose) unit is valid for a product of the given stock form. */
export function isUnitCompatible(unit: string, stockUnit: string): boolean {
  const a = allowedUnitsForProduct(stockUnit);
  return [...a.absolute, ...a.perArea, ...a.perCaldo].includes(unit);
}

/** Base unit of a rate (e.g. "L/ha" -> "L", "mL/100L" -> "mL", "kg" -> "kg"). */
export function baseUnitOf(unit: string): string {
  return (unit || "").replace(/\/\s*(ha|1000\s*m²|1000\s*m2|100\s*l)\s*$/i, "").trim() || unit;
}

/** Resolve the total caldo in liters from a task-level water volume input. */
export function resolveCaldoLiters(
  waterVolume: number | null | undefined,
  waterVolumeUnit: string | null | undefined,
  areaM2: number
): number | null {
  if (waterVolume == null || !(waterVolume > 0)) return null;
  if (waterVolumeUnit === "L/ha") {
    return areaM2 > 0 ? waterVolume * (areaM2 / 10000) : null;
  }
  // "L" (total) or anything else treated as absolute liters
  return waterVolume;
}

/**
 * ¿Estas dos unidades se contradicen de verdad?
 *
 * Solo cuando las DOS son medidas —volumen o masa— y no son la misma familia:
 * litros contra kilos no se convierten sin una densidad, así que una tarea así
 * nace imposible y hay que decirlo al elegir la dosis.
 *
 * «units» (paquetes, botes, bandejas) queda FUERA a propósito. Pedir «2
 * paquetes» de algo que se lleva en litros es ambiguo, pero es un uso real y la
 * app lleva desde siempre dejándolo pasar con el número tal cual —hay un test
 * que lo fija—. Rechazarlo ahora rompería tareas que hoy funcionan, y el
 * problema que se vino a arreglar no es ese.
 */
function unitsContradict(a: string | null | undefined, b: string | null | undefined): boolean {
  const from = normalizeUnit(a);
  const to = normalizeUnit(b);
  if (!from || !to) return false;            // desconocido: no se opina
  if (from === "units" || to === "units") return false; // envase, no medida
  return !sameFamily(from, to);
}

const round4 = (n: number): number => Math.round(n * 1e4) / 1e4;

export function resolveProductAmount(input: ResolveInput): ResolveResult {
  const { value, unit, areaM2, caldoL, stockUnit } = input;
  const basis = classifyUnit(unit);

  if (!(value > 0)) {
    return { amount: 0, amountUnit: unit, basis, error: "INVALID_VALUE" };
  }

  if (basis === "absolute") {
    /* Una cantidad absoluta se apunta SIEMPRE en la unidad del inventario: si el
       producto se lleva en kg y se piden 500 g, la tarea guarda 0,5 kg. Antes se
       guardaba el 500 con su «g» al lado, y todo lo que viene detrás comparaba ese
       500 con el 1 del stock: el aviso decía «no hay bastante» teniendo el doble, y
       completar la tarea habría descontado 500 kg de un bote de 1 kg.
       Si las unidades no son de la misma familia (pkg contra L, por ejemplo) no se
       inventa ninguna cifra: se deja el valor tal cual, como hasta ahora. */
    const from = normalizeUnit(unit);
    const to = normalizeUnit(stockUnit);
    if (from && to && from !== to) {
      const converted = convertQuantity(value, unit, stockUnit);
      if (converted != null) {
        return { amount: round4(converted), amountUnit: stockUnit, basis };
      }
      if (!unitsContradict(unit, stockUnit)) {
        return { amount: round4(value), amountUnit: unit, basis };
      }
      /* Familias distintas: LITROS contra KILOS. No hay conversión posible sin
         una densidad, así que devolver el número «tal cual» era lo peor de las
         tres opciones — la tarea nacía pidiendo 13,13 L de un producto que se
         lleva en kg, y el fallo no aparecía hasta el final, al completarla, con
         un «no se pudo descontar el inventario» que no dice nada.
         Caso real (26-08-2026, «Tratamiento — La Reina»): Oxicloruro de cobre,
         75,33 kg en inventario, la tarea pedía 13,13 L. Ni completar ni
         «registrar igualmente» podían funcionar nunca.
         Se dice AQUÍ, donde se elige la dosis, y no tres pantallas después. */
      return { amount: 0, amountUnit: stockUnit, basis, error: "UNIT_PRODUCT_MISMATCH" };
    }
    return { amount: round4(value), amountUnit: unit, basis };
  }

  if (basis === "per_area") {
    // Base unit is the prefix before "/ha" or "/1000m²" (e.g. "L/ha" -> "L", "mL/ha" -> "mL").
    const base = unit.replace(/\/\s*(ha|1000\s*m²|1000\s*m2)\s*$/i, "").trim();
    if (!(areaM2 > 0)) {
      return { amount: 0, amountUnit: base || "L", basis, error: "AREA_REQUIRED" };
    }
    /* Misma regla que en las absolutas: «0,8 L/ha» sobre un producto que se
       lleva en kg no es convertible, y dejarlo pasar fabrica una tarea que no se
       podrá completar jamás. Solo se rechaza cuando AMBAS unidades se reconocen
       y son de familias distintas; lo desconocido se deja pasar como siempre. */
    const baseNorm = normalizeUnit(base);
    const stockNorm = normalizeUnit(stockUnit);
    if (unitsContradict(base, stockUnit)) {
      return { amount: 0, amountUnit: stockUnit, basis, error: "UNIT_PRODUCT_MISMATCH" };
    }
    const per1000 = /1000\s*m(²|2)\s*$/i.test(unit);
    const factor = per1000 ? areaM2 / 1000 : areaM2 / 10000;
    /* Y si son de la misma familia pero distinta unidad (mL/ha sobre un producto
       en L), se convierte al terminar, igual que en las absolutas. */
    const raw = value * factor;
    if (baseNorm && stockNorm && baseNorm !== stockNorm) {
      const conv = convertQuantity(raw, base, stockUnit);
      if (conv != null) return { amount: round4(conv), amountUnit: stockUnit, basis };
    }
    return { amount: round4(raw), amountUnit: base || "L", basis };
  }

  // per_caldo
  if (!(caldoL != null && caldoL > 0)) {
    return { amount: 0, amountUnit: stockUnit, basis, error: "CALDO_REQUIRED" };
  }
  const numerator = norm(unit.split("/")[0]); // "ml"/"cc" or "g"
  const perCaldo = value * (caldoL / 100); // in numerator units (mL or g)

  if (numerator === "ml" || numerator === "cc") {
    if (!isLiquidStock(stockUnit)) {
      return { amount: 0, amountUnit: stockUnit, basis, error: "UNIT_PRODUCT_MISMATCH" };
    }
    const inLiters = norm(stockUnit) === "l";
    return {
      amount: round4(inLiters ? perCaldo / 1000 : perCaldo),
      amountUnit: inLiters ? "L" : stockUnit,
      basis,
    };
  }

  // g/100L
  if (!isSolidStock(stockUnit)) {
    return { amount: 0, amountUnit: stockUnit, basis, error: "UNIT_PRODUCT_MISMATCH" };
  }
  const inKg = norm(stockUnit) === "kg";
  return {
    amount: round4(inKg ? perCaldo / 1000 : perCaldo),
    amountUnit: inKg ? "kg" : stockUnit,
    basis,
  };
}

/**
 * ¿Esta receta necesita saber el caldo (agua) para poder resolverse?
 *
 * Una dosis «por cada 100 L» no es un número hasta que hay litros de agua: sin
 * caldo, `resolveProductAmount` devuelve 0 con CALDO_REQUIRED y la tarea nace
 * con el campo en rojo. Por eso la ficha del tratamiento exige un caldo DE
 * REFERENCIA en cuanto una de sus líneas se expresa así — que luego la tarea
 * puede cambiar, y al cambiarlo se recalculan las cantidades.
 */
export function requiresCaldo(rateUnits: Array<string | null | undefined>): boolean {
  return rateUnits.some((u) => classifyUnit(u || "") === "per_caldo");
}

/**
 * Reglas de una RECETA (las líneas de un tratamiento), aquí y no en el
 * componente para que se puedan probar sin montar React ni tocar la base.
 * Tipadas por forma, así que aceptan el borrador de la pantalla tal cual.
 */

/** Toda línea con producto y una dosis mayor que cero (y al menos una línea). */
export function componentsAreComplete(
  components: Array<{ product_id: string; amount: number }>,
): boolean {
  return components.length > 0 && components.every((c) => !!c.product_id && c.amount > 0);
}

/**
 * Falta el agua: hay una dosis «por cada 100 L» y la receta no dice de cuánta
 * agua habla. Es lo único que puede dejar una receta sin resolver, así que es
 * lo único que bloquea guardarla.
 */
export function caldoIsMissing(
  components: Array<{ rate_unit: string }>,
  caldoValue: string,
): boolean {
  return requiresCaldo(components.map((c) => c.rate_unit)) && !(parseFloat(caldoValue) > 0);
}
